import { useState } from 'react';
import dayjs from 'dayjs';
import { Button, Grid, Typography } from '@mui/material';
import { NotificationsActive, Alarm, Check } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useFeatureFlag } from 'providers';
import { ROUTES } from 'constants/routes';
import { VoucherList, Voucher, VoucherFilterOptions, VoucherStatus } from 'models';
import TagFilter from 'components/TagFilter';
import VoucherCard from './VoucherCard';

import {
  StyledBox,
  StyledContainer,
  StyledGrid,
  StyledPaper,
  EmptyPerkzWrapper,
  StyledGridContainer,
} from './PerkzWalletList.styles';

const tagFilterOptions: VoucherFilterOptions[] = [
  {
    id: 'active',
    label: 'Available',
    icon: <NotificationsActive />,
    isActive: true,
    isVisible: true,
  },
  {
    id: 'expired',
    label: 'Expired',
    icon: <Alarm />,
    isActive: false,
    isVisible: false,
  },
  {
    id: 'redeemed',
    label: 'Redeemed',
    icon: <Check />,
    isActive: false,
    isVisible: false,
  },
];

export interface PerkzWalletListProps {
  vouchers: VoucherList;
  onViewVoucher: (voucher: Voucher) => void;
}

const withinTimeLimitToShowOfferDelayCopy = () => {
  const memberSince = localStorage.getItem('memberSince');
  return memberSince && dayjs().diff(dayjs(memberSince), 'minute') < 30;
};

const shouldShowPerkzDelayCopy = (
  discountOfferOnSignUp: boolean,
  currentFilter: string,
  vouchers: VoucherList
) =>
  discountOfferOnSignUp &&
  currentFilter === 'active' &&
  vouchers.active.length <= 1 &&
  withinTimeLimitToShowOfferDelayCopy();

const EmptyPerkzCard = () => (
  <EmptyPerkzWrapper>
    <Typography>Visit the Perkz Store to get some Perkz.</Typography>
    <Button fullWidth color="primary" variant="contained" component={Link} to={ROUTES.PERKZ_STORE}>
      Visit Perkz Store
    </Button>
  </EmptyPerkzWrapper>
);

const PerkzWalletList = ({ vouchers, onViewVoucher }: PerkzWalletListProps) => {
  const [currentFilter, setCurrentFilter] = useState<VoucherStatus>('active');

  const handleOnFilter = (id: VoucherStatus) => setCurrentFilter(id);

  const discountOfferOnSignUp = useFeatureFlag('discountOfferOnSignUp');

  const showPerkzDelayCopy = shouldShowPerkzDelayCopy(
    discountOfferOnSignUp,
    currentFilter,
    vouchers
  );

  const filteredVouchers = vouchers[currentFilter];
  const showFilter = vouchers.expired.length || vouchers.redeemed.length;

  const filterOptions = tagFilterOptions.map((option) => {
    if (vouchers[option.id].length) {
      return { ...option, isVisible: true, isActive: option.id === currentFilter };
    }
    return option;
  });

  return (
    <StyledContainer aria-label="List of Purchased Perkz" id="perkz-wallet">
      <StyledPaper>
        <Typography variant="h1">Perkz Wallet</Typography>

        {showFilter ? <TagFilter options={filterOptions} onFilter={handleOnFilter} /> : null}

        <StyledGridContainer container spacing={3}>
          {filteredVouchers.length ? (
            filteredVouchers.map((voucher: Voucher) => (
              <StyledGrid
                item
                xs={12}
                sm={6}
                key={`purchased-${currentFilter}-${voucher.voucherId}`}
              >
                <VoucherCard voucher={voucher} onClick={onViewVoucher} />
              </StyledGrid>
            ))
          ) : (
            <Grid item xs={12}>
              <EmptyPerkzCard />
            </Grid>
          )}
          {showPerkzDelayCopy && (
            <StyledBox>
              <Typography variant="body1" gutterBottom>
                <strong>Note: </strong> it may take up to 15 minutes for offers to appear in your
                Perkz Wallet
              </Typography>
            </StyledBox>
          )}
        </StyledGridContainer>
      </StyledPaper>
    </StyledContainer>
  );
};

export default PerkzWalletList;
